import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { SERVICE_OPTIONS, StarPicker } from "@/components/review-utils";

export default function ReviewForm({ onSubmitted }) {
  const [form, setForm] = useState({
    name: "",
    rating: 5,
    text: "",
    city: "",
    state: "",
    service: "general",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.text.trim().length < 10) {
      toast.error("Please add your name and a review of at least 10 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/reviews", {
        name: form.name.trim(),
        rating: Number(form.rating),
        text: form.text.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        service: form.service,
      });
      setDone(true);
      toast.success("Thank you. Your review will appear after a quick approval.");
      setForm({ name: "", rating: 5, text: "", city: "", state: "", service: "general" });
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="review-form-success" className="card-luxury p-10 text-center">
        <div className="overline mb-4">— Thank You</div>
        <h3 className="font-serif text-3xl text-white mb-3">Your review has been received.</h3>
        <p className="text-white/65 leading-relaxed mb-6">
          We review every story before it goes live. Yours will appear shortly.
        </p>
        <button
          data-testid="review-form-another"
          onClick={() => setDone(false)}
          className="text-[#D4AF37] text-xs uppercase tracking-[0.22em] border-b border-[#D4AF37]/40 pb-1 hover:gap-4 transition-all inline-flex items-center gap-2"
        >
          Leave Another <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <form data-testid="review-form" onSubmit={submit} className="card-luxury p-8 md:p-10 space-y-8">
      <div>
        <div className="overline mb-4">Your Rating</div>
        <StarPicker value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <label className="overline mb-3 block">Full Name</label>
          <input
            type="text"
            data-testid="review-input-name"
            value={form.name}
            onChange={update("name")}
            className="input-luxury"
            placeholder="Jane D."
            required
          />
        </div>
        <div>
          <label className="overline mb-3 block">Service</label>
          <div className="relative">
            <select
              data-testid="review-input-service"
              value={form.service}
              onChange={update("service")}
              className="input-luxury appearance-none pr-8 cursor-pointer"
              style={{ colorScheme: "dark" }}
            >
              {SERVICE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value} style={{ background: "#1A1A1A" }}>
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-[#D4AF37] absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <label className="overline mb-3 block">City (optional)</label>
          <input
            type="text"
            data-testid="review-input-city"
            value={form.city}
            onChange={update("city")}
            className="input-luxury"
            placeholder="Houston"
          />
        </div>
        <div>
          <label className="overline mb-3 block">State (optional)</label>
          <input
            type="text"
            data-testid="review-input-state"
            value={form.state}
            onChange={update("state")}
            className="input-luxury"
            placeholder="TX"
            maxLength={40}
          />
        </div>
      </div>

      <div>
        <label className="overline mb-3 block">Your Review</label>
        <textarea
          data-testid="review-input-text"
          value={form.text}
          onChange={update("text")}
          rows={5}
          className="input-luxury resize-none"
          placeholder="Share your experience working with Impact Legacy Group..."
          minLength={10}
          maxLength={2000}
          required
        />
        <div className="text-xs text-white/40 mt-2">{form.text.length}/2000</div>
      </div>

      <button
        type="submit"
        data-testid="review-submit"
        disabled={loading}
        className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? "Submitting..." : (<>Submit Review <ArrowRight className="w-4 h-4" /></>)}
      </button>
    </form>
  );
}
